import React from "react";

import { Button } from "@proda-ai/ui-kit";

import type { BoxProps } from "@/ui/atoms/Box";
import Box from "@/ui/atoms/Box";
import ErrorMessage from "@/ui/atoms/ErrorMessage";

import type { GridApi } from "@/ui/molecules/Grid";
import { useLazyGridModuleExcelExport } from "@/ui/molecules/Grid";
import TopBarOverview from "@/ui/molecules/TopBarOverview";
import GridLoader from "@/ui/molecules/GridLoader";

import GridChartOfAccounts from "@/ui/organisms/GridChartOfAccounts";

import LayoutFillParentCentered from "@/ui/layouts/LayoutFillParentCentered";
import LayoutFillParentWithFooter from "@/ui/layouts/LayoutFillParentWithFooter";

const DEFAULT_COA_ID = 1;

export type ViewChartsOfAccountsDetailsParams = {
    useState: ({ coaId }: { coaId: number }) => {
        allCoAs:
            | {
                  coaId: number;
                  name: string;
                  description: string;
                  ownerCompanyId?: number | null;
              }[]
            | null;
        allCoAsRequestStatus: "idle" | "pending" | "rejected" | "resolved";
        accounts:
            | {
                  account: string;
                  code: string;
                  subcategory: string | null;
                  type: "income" | "expense" | "non-operating";
                  section: string | null;
              }[]
            | null;
        accountsRequestStatus: "idle" | "pending" | "rejected" | "resolved";
        isGlobalAdmin: boolean;
        userCompanyId: number | null;
        isProdaUser: boolean;
    };
    useCases: () => {
        navigateToChartOfAccountsEditDetails: (coaId: number) => void;
    };
};

export type ViewChartsOfAccountsDetailsProps = BoxProps & {
    coaId: number;
    onClickBackButton: () => void;
    onClickEditMappingsButton: (params: { coaId: number }) => void;
    onClickViewAuditLog: (params: { coaId: number }) => void;
};

export const createViewChartsOfAccountsDetails = ({
    useState,
    useCases,
}: ViewChartsOfAccountsDetailsParams) => {
    const ViewChartsOfAccountsDetails = ({
        coaId,
        onClickBackButton,
        onClickEditMappingsButton,
        onClickViewAuditLog,
        ...props
    }: ViewChartsOfAccountsDetailsProps) => {
        const { ExcelExportModule, isExcelModuleLoading, excelModuleError } =
            useLazyGridModuleExcelExport();

        const {
            allCoAs,
            allCoAsRequestStatus,
            accounts,
            accountsRequestStatus,
            isGlobalAdmin,
            userCompanyId,
            isProdaUser,
        } = useState({ coaId });
        useCases();

        const [gridApi, setGridApi] = React.useState<GridApi | null>(null);

        const coa = allCoAs?.find(coa => coa.coaId === coaId);
        const noMatch = Boolean(allCoAs && allCoAs.length > 0 && !coa);

        const isLoading =
            allCoAsRequestStatus === "pending" ||
            accountsRequestStatus === "pending" ||
            isExcelModuleLoading ||
            allCoAsRequestStatus === "idle" ||
            accountsRequestStatus === "idle";
        const isError =
            allCoAsRequestStatus === "rejected" ||
            accountsRequestStatus === "rejected" ||
            Boolean(excelModuleError) ||
            noMatch;

        const coaOwnerCompanyId = coa?.ownerCompanyId;
        const coaCanBeEdited = React.useMemo(() => {
            if (!isProdaUser) return false;

            if (coaId === DEFAULT_COA_ID) return false;
            if (isGlobalAdmin) return true;
            if (userCompanyId === null) return false;
            return coaOwnerCompanyId === userCompanyId;
        }, [
            isProdaUser,
            coaId,
            isGlobalAdmin,
            userCompanyId,
            coaOwnerCompanyId,
        ]);

        const gridElement = React.useMemo(() => {
            return (
                <>
                    {isLoading && <GridLoader />}

                    {!isLoading && isError && (
                        <ErrorMessage>
                            Error loading chart of accounts details
                        </ErrorMessage>
                    )}

                    {!isLoading &&
                        !isError &&
                        !noMatch &&
                        ExcelExportModule &&
                        accounts !== null && (
                            <GridChartOfAccounts
                                rowData={accounts}
                                modules={[ExcelExportModule]}
                                onGridReady={params => {
                                    setGridApi(params.api);
                                }}
                            />
                        )}
                </>
            );
        }, [accounts, isError, isLoading, noMatch, ExcelExportModule]);

        return (
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    width: "100%",
                    height: "100%",
                    ...(props.sx || {}),
                }}
                {...props}
            >
                <TopBarOverview
                    title={coa?.name}
                    backButtonLabel="Back to Charts of Accounts dashboard"
                    onBackButtonClick={onClickBackButton}
                />
                {isProdaUser || coaCanBeEdited ? (
                    <LayoutFillParentWithFooter
                        footerContent={
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "flex-end",
                                    gap: "16px",
                                    padding: "16px 24px",
                                    borderTop:
                                        "1px solid var(--proda-neutral-200)",
                                }}
                            >
                                <Button
                                    pattern="secondary"
                                    size={"medium"}
                                    disabled={!gridApi}
                                    onClick={() => {
                                        if (gridApi) {
                                            gridApi.exportDataAsExcel({
                                                sheetName:
                                                    coa?.name ?? "CoA Details",
                                            });
                                        }
                                    }}
                                >
                                    Export as Excel
                                </Button>
                                <Button
                                    pattern="secondary"
                                    size={"medium"}
                                    disabled={isError || isLoading}
                                    onClick={() => {
                                        onClickViewAuditLog({
                                            coaId,
                                        });
                                    }}
                                >
                                    View audit log
                                </Button>
                                {coaCanBeEdited && (
                                    <Button
                                        pattern="primary"
                                        size={"medium"}
                                        disabled={isError || isLoading}
                                        onClick={() => {
                                            onClickEditMappingsButton({
                                                coaId,
                                            });
                                        }}
                                    >
                                        Edit mappings
                                    </Button>
                                )}
                            </Box>
                        }
                    >
                        <LayoutFillParentCentered>
                            {gridElement}
                        </LayoutFillParentCentered>
                    </LayoutFillParentWithFooter>
                ) : (
                    <LayoutFillParentCentered>
                        {gridElement}
                    </LayoutFillParentCentered>
                )}
            </Box>
        );
    };

    return ViewChartsOfAccountsDetails;
};
